import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { DurationUnit } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { sweetpassCheckoutContentfulDataFallback } from './sweetpassCheckoutContentfulDataFallback';

export const useSweetpassCheckoutContentfulData = (
  props: UseSweetpassCheckoutContentfulDataProps,
) => {
  const {
    planPrice,
    trialDuration = '',
    trialDurationSingular = '',
    billingFrequencyUnit = DurationUnit.Month,
  } = props;

  const {
    isLoading,
    title,
    termsAndConditions,
    cta,
    freeTrialTitle,
    freeTrialTermsAndConditions,
    freeTrialCta,
  } = useContentfulDataWithFallback();

  const { formatDate, formatPrice } = useLocalizationContext();
  const price = planPrice ? formatPrice(planPrice * 100, 'USD') : '';
  const date = formatDate(new Date());

  const content = useMemo(() => {
    const interpolateValues = (field: string) => {
      return field
        .replaceAll('{trialDuration}', trialDuration)
        .replaceAll('{trialDurationSingular}', trialDurationSingular)
        .replaceAll('{price}', price)
        .replaceAll('{date}', date)
        .replaceAll(
          '{billingFrequencyUnit}',
          billingFrequencyUnit?.toLowerCase(),
        );
    };

    return {
      title,
      cta,
      freeTrialTitle,
      freeTrialCta,
      termsAndConditions: interpolateValues(termsAndConditions),
      freeTrialTermsAndConditions: interpolateValues(
        freeTrialTermsAndConditions,
      ),
    };
  }, [
    title,
    cta,
    freeTrialTitle,
    freeTrialCta,
    termsAndConditions,
    freeTrialTermsAndConditions,
    trialDuration,
    trialDurationSingular,
    price,
    date,
    billingFrequencyUnit,
  ]);

  return { content, isLoading };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPE = 'sweetpassCheckoutPage';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function useContentfulDataWithFallback() {
  const { data: contentfulData, fetching: isLoading } =
    useContentfulContentTypeEntry<ContentfulSweetpassCheckoutPage>({
      contentType: CONTENT_TYPE,
      include: 2,
    });

  const requestedData = contentfulData?.fields;
  const fallbackData = sweetpassCheckoutContentfulDataFallback.fields;

  return {
    isLoading,
    title: requestedData?.title ?? fallbackData.title,
    termsAndConditions:
      requestedData?.termsAndConditionsV2 ?? fallbackData.termsAndConditionsV2,
    cta: requestedData?.cta ?? fallbackData.cta,
    freeTrialTitle:
      requestedData?.freeTrialTitle ?? fallbackData.freeTrialTitle,
    freeTrialTermsAndConditions:
      requestedData?.freeTrialTermsAndConditionsV2 ??
      fallbackData?.freeTrialTermsAndConditionsV2,
    freeTrialCta: requestedData?.freeTrialCta ?? fallbackData.freeTrialCta,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassCheckoutContentfulDataProps = Readonly<{
  planPrice?: number;
  trialDuration?: string;
  trialDurationSingular?: string;
  billingFrequencyUnit?: DurationUnit;
}>;

type ContentfulSweetpassCheckoutPage = Readonly<{
  title: string;
  termsAndConditionsV2: string;
  cta: string;
  freeTrialTitle: string;
  freeTrialTermsAndConditionsV2: string;
  freeTrialCta: string;
}>;
